import { BehaviorSubject, filter, from, map, switchMap } from "rxjs";
import { getDb, type RecordDoc } from "@/lib/db";
import { profile$ } from "@/lib/profile";
import type { JobData, JobRecord } from "@/types/domain";

const state$ = new BehaviorSubject<JobRecord[]>([]);

function toJobRecord(doc: RecordDoc): JobRecord {
  return {
    id: doc.id,
    type: doc.type,
    family_id: doc.family_id,
    user_id: doc.user_id,
    created_at: doc.created_at,
    updated_at: doc.updated_at,
    data: doc.data as unknown as JobData,
  };
}

profile$
  .pipe(
    filter((s) => !s.loading && s.profile !== null),
    switchMap((s) =>
      from(getDb()).pipe(
        switchMap(
          (db) =>
            db.week_allowance.find({
              selector: {
                family_id: s.profile!.data.family_id,
                type: "job",
              },
            }).$,
        ),
      ),
    ),
    map((docs) =>
      docs
        .map((d) => toJobRecord(d.toJSON() as RecordDoc))
        .filter((r) => r.data.is_active)
        .sort((a, b) => a.data.sort_order - b.data.sort_order),
    ),
  )
  .subscribe((jobs) => state$.next(jobs));

export const jobsService = {
  jobs$: state$.asObservable(),

  get jobs(): JobRecord[] {
    return state$.getValue();
  },

  async createJob(
    familyId: string,
    jobData: Omit<JobData, "is_active" | "sort_order">,
  ): Promise<void> {
    const db = await getDb();
    const data: JobData = {
      ...jobData,
      is_active: true,
      sort_order: state$.getValue().length,
    };
    const now = new Date().toISOString();
    await db.week_allowance.insert({
      id: crypto.randomUUID(),
      type: "job",
      family_id: familyId,
      user_id: null,
      created_at: now,
      updated_at: now,
      data: data as unknown as Record<string, unknown>,
    });
  },

  async updateJob(id: string, patch: Partial<JobData>): Promise<void> {
    const db = await getDb();
    const doc = await db.week_allowance.findOne(id).exec();
    if (!doc) return;
    const json = doc.toJSON() as RecordDoc;
    const data = { ...(json.data as unknown as JobData), ...patch };
    await doc.patch({
      data: data as unknown as Record<string, unknown>,
      updated_at: new Date().toISOString(),
    });
  },

  async archiveJob(id: string): Promise<void> {
    await jobsService.updateJob(id, { is_active: false });
  },
};
